import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { currentIsoDate } from './date-time-field.util';

type CalendarDay = {
  isoDate: string;
  label: number;
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
};

@Component({
  selector: 'lib-date-calendar',
  standalone: true,
  template: `
    <section class="date-calendar" role="dialog" aria-label="Choose date">
      <div class="date-calendar__header">
        <button type="button" class="date-calendar__nav" aria-label="Previous month" (click)="moveMonth(-1)">
          ‹
        </button>
        <div class="date-calendar__month">{{ monthLabel }}</div>
        <button type="button" class="date-calendar__nav" aria-label="Next month" (click)="moveMonth(1)">
          ›
        </button>
      </div>

      <div class="date-calendar__weekdays" aria-hidden="true">
        @for (day of weekdayLabels; track day) {
          <span>{{ day }}</span>
        }
      </div>

      <div class="date-calendar__grid">
        @for (day of days; track day.isoDate) {
          <button
            type="button"
            class="date-calendar__day"
            [class.date-calendar__day--muted]="!day.inMonth"
            [class.date-calendar__day--today]="day.isToday"
            [class.date-calendar__day--selected]="day.isSelected"
            [disabled]="day.isDisabled"
            (click)="selectDate(day)"
          >
            {{ day.label }}
          </button>
        }
      </div>
    </section>
  `,
  styles: [`
    .date-calendar {
      width: 280px;
      border: 1px solid #dbe3ef;
      border-radius: 10px;
      background: #ffffff;
      box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
      padding: 10px;
      color: #111827;
      box-sizing: border-box;
    }

    .date-calendar__header {
      display: grid;
      grid-template-columns: 32px 1fr 32px;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .date-calendar__month {
      text-align: center;
      font-size: 14px;
      font-weight: 700;
      color: #1f2937;
      text-transform: capitalize;
    }

    .date-calendar__nav {
      width: 32px;
      height: 32px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #ffffff;
      color: #1f2937;
      cursor: pointer;
      font-size: 20px;
      line-height: 1;
    }

    .date-calendar__weekdays,
    .date-calendar__grid {
      display: grid;
      grid-template-columns: repeat(7, minmax(0, 1fr));
      gap: 4px;
    }

    .date-calendar__weekdays {
      margin-bottom: 4px;
      color: #64748b;
      font-size: 11px;
      font-weight: 700;
      text-align: center;
      text-transform: uppercase;
    }

    .date-calendar__day {
      aspect-ratio: 1;
      min-width: 0;
      border: 1px solid transparent;
      border-radius: 8px;
      background: transparent;
      color: #111827;
      cursor: pointer;
      font: inherit;
      font-size: 13px;
    }

    .date-calendar__day:hover:not(:disabled) {
      border-color: #93c5fd;
      background: #eff6ff;
    }

    .date-calendar__day--muted {
      color: #94a3b8;
    }

    .date-calendar__day--today {
      border-color: #bfdbfe;
      color: #1d4ed8;
      font-weight: 700;
    }

    .date-calendar__day--selected {
      border-color: #2563eb;
      background: #2563eb;
      color: #ffffff;
      font-weight: 700;
    }

    .date-calendar__day:disabled {
      color: #cbd5e1;
      cursor: default;
      text-decoration: line-through;
    }
  `],
})
export class DateCalendarComponent implements OnChanges {
  @Input() public selectedDate = '';
  @Input() public min: string | null = null;
  @Input() public max: string | null = null;

  @Output() public readonly selectedDateChange = new EventEmitter<string>();

  protected readonly weekdayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  protected visibleYear = new Date().getFullYear();
  protected visibleMonth = new Date().getMonth();

  protected get monthLabel(): string {
    return new Intl.DateTimeFormat(undefined, {
      month: 'long',
      year: 'numeric',
    }).format(new Date(this.visibleYear, this.visibleMonth, 1));
  }

  protected get days(): CalendarDay[] {
    const firstOfMonth = new Date(this.visibleYear, this.visibleMonth, 1);
    const mondayBasedOffset = (firstOfMonth.getDay() + 6) % 7;
    const gridStart = new Date(this.visibleYear, this.visibleMonth, 1 - mondayBasedOffset);
    const today = currentIsoDate();
    const days: CalendarDay[] = [];

    for (let index = 0; index < 42; index += 1) {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);

      const isoDate = this.toIsoDate(date);
      days.push({
        isoDate,
        label: date.getDate(),
        inMonth: date.getMonth() === this.visibleMonth,
        isToday: isoDate === today,
        isSelected: isoDate === this.selectedDate,
        isDisabled: this.isDisabled(isoDate),
      });
    }

    return days;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDate']) {
      this.syncVisibleMonth();
    }
  }

  protected moveMonth(delta: number): void {
    const next = new Date(this.visibleYear, this.visibleMonth + delta, 1);
    this.visibleYear = next.getFullYear();
    this.visibleMonth = next.getMonth();
  }

  protected selectDate(day: CalendarDay): void {
    if (day.isDisabled) {
      return;
    }

    this.selectedDateChange.emit(day.isoDate);
  }

  private syncVisibleMonth(): void {
    const source = this.selectedDate || currentIsoDate();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(source);

    if (!match) {
      return;
    }

    this.visibleYear = Number(match[1]);
    this.visibleMonth = Number(match[2]) - 1;
  }

  private isDisabled(isoDate: string): boolean {
    if (this.min && isoDate < this.min) {
      return true;
    }

    if (this.max && isoDate > this.max) {
      return true;
    }

    return false;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear().toString().padStart(4, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
